import type { MutableRefObject, PropsWithRef } from 'react'
import { useState } from 'react'

import {
  IconDuplicate,
  SubText,
  TransparentIconButton,
  WithTooltip,
} from '@procurify/ui'
import copy from 'copy-to-clipboard'

import { useProcurifyIntl } from '@webapp/hooks'
import { type Enum } from '@webapp/utils/enums'
import messages from './messages'

interface ICopyToClipboardButtonProps {
  value?: string
  sourceRef?: MutableRefObject<HTMLInputElement>
  ariaLabel?: string
}

export const CopyToClipboardButton = ({
  value,
  sourceRef,
  ariaLabel,
}: PropsWithRef<ICopyToClipboardButtonProps>) => {
  const intl = useProcurifyIntl()

  const TOOLTIP_TEXT = {
    INITIAL: intl.formatMessage(messages.tooltipTextInitial),
    COPIED: intl.formatMessage(messages.tooltipTextCopied),
  } as const

  const [open, setOpen] = useState(false)

  const [tooltipText, setTooltipText] = useState<Enum<typeof TOOLTIP_TEXT>>(
    TOOLTIP_TEXT.INITIAL
  )

  const onCopy = () => {
    if (value) {
      copy(value)
    } else if (sourceRef) {
      copy(sourceRef.current.value)
    }
  }

  return (
    <WithTooltip
      open={open}
      onOpen={() => {
        if (!open) {
          setTooltipText(TOOLTIP_TEXT.INITIAL)
          setOpen(true)
        }
      }}
      onClose={() => {
        setOpen(false)
      }}
      title={<SubText>{tooltipText}</SubText>}
    >
      <WithTooltip.Wrapper>
        <TransparentIconButton
          iconComponent={<IconDuplicate />}
          onClick={() => {
            onCopy()
            setTooltipText(TOOLTIP_TEXT.COPIED)
          }}
          aria-label={
            ariaLabel || intl.formatMessage(messages.tooltipTextInitial)
          }
        />
      </WithTooltip.Wrapper>
    </WithTooltip>
  )
}
